import { between, clamp01, headingOf, smooth, turnToward, type CritterWorld, type Random } from './critter-math.js';
import { restingAction, type BirdAction } from './bird-model.js';
import type { BirdSpecies } from './bird-species.js';

const HOME_RADIUS = 7;
/** Seconds spent coiling before the spring, and in flight before a bird is cruising. */
const COIL = .45;

export type BirdMode = 'idle' | 'walk' | 'launch' | 'fly' | 'land';

/**
 * A bird's wits, with the pace and nerve of its species. On the ground it stands about looking, pecking and preening, and now and then struts a few metres; if
 * something comes close it coils and springs into the air, climbs away and flies off. It can also arrive: gliding in from
 * above and flaring to land. The brain moves it and describes what it is doing; `BirdModel` turns that into joints.
 */
export class BirdBrain {
  readonly action: BirdAction = restingAction();
  mode: BirdMode = 'idle';
  /** Height above the ground. Zero while walking or standing. */
  y = 0;
  flightTime = 0;
  private idleTime = 0; private idleLength: number;
  private goal: { x: number; z: number } | null = null;
  private launchT = 0; private course = 0; private cruise = 8; private speed = 0; private wander = 0;
  private landing = { fromX: 0, fromZ: 0, fromY: 0, toX: 0, toZ: 0, t: 0, duration: 1 };
  private bank = 0;

  constructor(readonly species: BirdSpecies, public x: number, public z: number, public heading: number, private home: { x: number; z: number }, private random: Random) {
    this.action.idle = random() * 40; this.idleLength = between(random, 1.5, 5); this.wander = random() * 6.28;
  }

  /** High enough that the fog veil should not cover it. */
  get airborne() { return this.y > 2; }

  /** Starts above and away from where it stands, then glides in to land there. */
  arrive(fromX: number, fromZ: number, fromY: number, duration: number) {
    this.landing = { fromX, fromZ, fromY, toX: this.x, toZ: this.z, t: 0, duration };
    this.heading = headingOf(this.x - fromX, this.z - fromZ);
    this.x = fromX; this.z = fromZ; this.y = fromY; this.mode = 'land';
    Object.assign(this.action, { air: 1, effort: .6 });
  }

  /** Springs into the air on a heading, climbing to a cruising height. */
  depart(heading: number) {
    if (this.mode === 'launch' || this.mode === 'fly' || this.mode === 'land') return;
    this.mode = 'launch'; this.launchT = 0; this.course = heading; this.cruise = between(this.random, 6.5, 10.5); this.goal = null;
  }

  update(dt: number, world: CritterWorld) {
    const a = this.action;
    a.idle += dt;
    switch (this.mode) {
      case 'idle': case 'walk': this.onGround(dt, world); break;
      case 'launch': this.launch(dt); break;
      case 'fly': this.fly(dt); break;
      case 'land': this.land(dt); break;
    }
  }

  private onGround(dt: number, world: CritterWorld) {
    const a = this.action;
    const threat = world.threat(this.x, this.z, this.species.scareReach);
    if (threat) { this.depart(headingOf(this.x - threat.x, this.z - threat.z) + between(this.random, -.5, .5)); return; }
    a.walk += ((this.mode === 'walk' ? 1 : 0) - a.walk) * (1 - Math.exp(-dt * (this.mode === 'walk' ? 7 : 9)));
    a.air = 0; a.flare = 0; a.crouch = 0; a.effort = 0; a.pitch = 0; a.bank = 0;
    if (this.mode === 'idle') {
      this.idleTime += dt;
      if (this.idleTime < this.idleLength) return;
      this.idleTime = 0; this.idleLength = between(this.random, 2, 6.5);
      if (this.random() < .55) this.planWalk(world);
      return;
    }
    const goal = this.goal;
    if (!goal) { this.mode = 'idle'; return; }
    const toward = headingOf(goal.x - this.x, goal.z - this.z), distance = Math.hypot(goal.x - this.x, goal.z - this.z);
    this.heading = turnToward(this.heading, toward, 4 * dt);
    const step = Math.min(distance, this.species.walkSpeed * a.walk * dt), dx = Math.sin(this.heading), dz = Math.cos(this.heading);
    if (distance < .25 || !world.free(this.x + dx * .9, this.z + dz * .9, .35)) { this.mode = 'idle'; this.goal = null; return; }
    this.x += dx * step; this.z += dz * step; a.stride += step / this.species.strideLength;
  }

  private planWalk(world: CritterWorld) {
    for (let tries = 0; tries < 6; tries++) {
      const angle = this.random() * Math.PI * 2, reach = between(this.random, 1.5, HOME_RADIUS);
      const goal = { x: this.home.x + Math.cos(angle) * reach, z: this.home.z + Math.sin(angle) * reach };
      if (Math.hypot(goal.x - this.x, goal.z - this.z) < 1.2 || !world.free(goal.x, goal.z, .4)) continue;
      this.goal = goal; this.mode = 'walk'; return;
    }
  }

  private launch(dt: number) {
    const a = this.action;
    this.launchT += dt;
    this.heading = turnToward(this.heading, this.course, 8 * dt);
    a.walk *= Math.exp(-dt * 12); a.crouch = smooth(this.launchT / (COIL * .8));
    if (this.launchT >= COIL) { this.mode = 'fly'; this.flightTime = 0; this.speed = 1.4; this.y = .05; }
  }

  private fly(dt: number) {
    const a = this.action;
    this.flightTime += dt;
    const t = this.flightTime;
    this.speed = Math.min(this.species.cruiseSpeed, this.speed + 3.4 * dt);
    const previousHeading = this.heading;
    this.heading = turnToward(this.heading, this.course + Math.sin(t * .8 + this.wander) * .28, 1.8 * dt);
    const rise = Math.min(4.6, Math.max(0, (this.cruise - this.y) * 1.4));
    this.y += (rise + (t < .4 ? 2.2 : 0)) * dt;
    this.x += Math.sin(this.heading) * this.speed * dt; this.z += Math.cos(this.heading) * this.speed * dt;
    // Wing beats: all-out through the take-off and climb, then loosening into a flap-and-glide rhythm.
    const settled = smooth((t - 2) / 2);
    a.air = smooth(t / .22); a.crouch = 1 - smooth(t / .2); a.flare = 0;
    a.effort = 1 - settled * this.species.glide * (.5 - .5 * Math.sin(t * .6 + this.wander));
    a.flap += dt * (this.species.flapRate[0] + a.effort * this.species.flapRate[1]);
    a.pitch = Math.atan2(rise, Math.max(this.speed, 1)) * .8 + (1 - settled) * .1;
    const turn = (this.heading - previousHeading) / Math.max(dt, 1e-3);
    this.bank += (Math.max(-.6, Math.min(.6, turn * .7)) - this.bank) * (1 - Math.exp(-dt * 4));
    a.bank = this.bank;
  }

  private land(dt: number) {
    const a = this.action, l = this.landing;
    l.t += dt;
    const p = clamp01(l.t / l.duration), e = 1 - (1 - p) ** 1.7;
    this.x = l.fromX + (l.toX - l.fromX) * e; this.z = l.fromZ + (l.toZ - l.fromZ) * e;
    this.y = l.fromY * (1 - e) ** 1.2;
    const flare = smooth((p - .6) / .32);
    a.air = 1 - smooth((p - .92) / .08); a.flare = flare; a.effort = .5 + .5 * flare; a.crouch = 0; a.walk = 0;
    a.pitch = -.2 * (1 - flare) + .7 * flare; a.bank = 0;
    a.flap += dt * (9 + 7 * flare);
    if (p >= 1) { this.mode = 'idle'; this.y = 0; this.idleTime = 0; this.idleLength = between(this.random, 1.5, 3.5); Object.assign(a, { air: 0, flare: 0, effort: 0, pitch: 0 }); }
  }
}
